<script>
import Draggable from 'vuedraggable';
import TrainingContentDropdown from './TrainingContentDropdown.vue';
import TrainingAIStructureModal from './TrainingAIStructureModal.vue';

export default {
  components: {
    Draggable,
    TrainingContentDropdown,
  },
  props: {
    training: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      isEditorOpen: false,
      items: [],
    };
  },
  methods: {
    resetForm() {
      this.isLoading = false;
      this.items = [];
    },
    confirmClose() {
      if (this.items.length > 0) {
        this.$buefy.dialog.confirm({
          title: 'Confirmation',
          message: 'Vous avez des éléments non sauvegardés. Voulez-vous vraiment fermer sans créer la structure ?',
          confirmText: 'Oui, fermer',
          cancelText: 'Annuler',
          type: 'is-warning',
          onConfirm: () => {
            this.$parent.close();
          },
        });
      } else {
        this.$parent.close();
      }
    },
    addChapter() {
      this.items.push({
        id: Date.now(),
        name: '',
        type: 'SECTION',
        children: [],
      });

      this.$nextTick(() => this.$refs.modalBody.scroll(0, this.$refs.modalBody.scrollHeight));
    },
    removeItem(index) {
      this.items.splice(index, 1);
    },
    addLesson(sectionIndex) {
      if (sectionIndex != null) {
        const id = Date.now();
        this.items[sectionIndex].children.push({
          id,
          name: '',
          type: 'GENERIC',
        });

        this.$nextTick(() => this.$refs[`lesson-title-${id}`][0].$refs.input.focus());
      } else {
        this.items.push({
          id: Date.now(),
          name: '',
          type: 'GENERIC',
        });
      }

      this.$nextTick(() => this.$refs.modalBody.scroll(0, this.$refs.modalBody.scrollHeight));
    },
    addQuiz(sectionIndex = null) {
      const newItem = {
        id: Date.now(),
        name: '',
        type: 'QUIZ',
      };

      if (sectionIndex != null) {
        this.items[sectionIndex].children.push(newItem);
      } else {
        this.items.push(newItem);
      }

      this.$nextTick(() => this.$refs.modalBody.scroll(0, this.$refs.modalBody.scrollHeight));
    },
    addTasks(sectionIndex = null) {
      const newItem = {
        id: Date.now(),
        name: '',
        type: 'TASKS',
      };

      if (sectionIndex != null) {
        this.items[sectionIndex].children.push(newItem);
      } else {
        this.items.push(newItem);
      }

      this.$nextTick(() => this.$refs.modalBody.scroll(0, this.$refs.modalBody.scrollHeight));
    },
    addAssignment(sectionIndex = null) {
      const newItem = {
        id: Date.now(),
        name: '',
        type: 'ASSIGNMENT',
      };

      if (sectionIndex != null) {
        this.items[sectionIndex].children.push(newItem);
      } else {
        this.items.push(newItem);
      }

      this.$nextTick(() => this.$refs.modalBody.scroll(0, this.$refs.modalBody.scrollHeight));
    },
    removeLesson(sectionIndex, lessonIndex) {
      this.items[sectionIndex].children.splice(lessonIndex, 1);
    },
    openAIGenerator() {
      this.$buefy.modal.open({
        parent: this,
        component: TrainingAIStructureModal,
        hasModalCard: true,
        trapFocus: true,
        props: {
          training: this.training,
        },
        events: {
          'structure-generated': (generatedItems) => {
            this.items = generatedItems;
          },
        },
      });
    },
    async createStructure() {
      const isValid = this.validateStructure();
      if (!isValid) return;

      this.isLoading = true;

      try {
        const cleanStructure = this.items.map((item) => ({
          name: item.name,
          type: item.type,
          children: item.children ? item.children.map((child) => ({
            name: child.name,
            type: child.type,
          })) : [],
        }));

        await this.$store.dispatch('trainings/createItemStructure', {
          uuid: this.training.uuid,
          structure: cleanStructure,
        });

        this.$buefy.toast.open({
          message: 'Structure créée avec succès !',
          type: 'is-success',
        });

        this.$emit('close');
      } catch (error) {
        console.error('Error creating structure:', error);
        this.$buefy.toast.open({
          message: (error.response && error.response.data && error.response.data.message) || 'Erreur lors de la création de la structure',
          type: 'is-danger',
        });
      } finally {
        this.isLoading = false;
      }
    },
    validateStructure() {
      for (const item of this.items) {
        if (!item.name.trim()) {
          let message;
          switch (item.type) {
            case 'SECTION':
              message = 'Tous les chapitres doivent avoir un titre';
              break;
            case 'QUIZ':
              message = 'Tous les quiz doivent avoir un titre';
              break;
            case 'TASKS':
              message = 'Toutes les listes de tâches doivent avoir un titre';
              break;
            case 'ASSIGNMENT':
              message = 'Tous les devoirs doivent avoir un titre';
              break;
            default:
              message = 'Toutes les leçons doivent avoir un titre';
          }
          this.$buefy.toast.open({
            message,
            type: 'is-warning',
          });
          return false;
        }

        if (item.children) {
          for (const lesson of item.children) {
            if (!lesson.name.trim()) {
              this.$buefy.toast.open({
                message: 'Toutes les leçons doivent avoir un titre',
                type: 'is-warning',
              });
              return false;
            }
          }
        }
      }

      return true;
    },
    getItemLabel(type) {
      switch (type) {
        case 'QUIZ':
          return 'Titre du quiz';
        case 'TASKS':
          return 'Titre de la liste de tâches';
        case 'ASSIGNMENT':
          return 'Titre du devoir';
        default:
          return 'Titre de la leçon';
      }
    },
    getItemPlaceholder(type) {
      switch (type) {
        case 'QUIZ':
          return 'Ex: Quiz de révision chapitre 1';
        case 'TASKS':
          return 'Ex: Exercices pratiques';
        case 'ASSIGNMENT':
          return 'Ex: Projet final à rendre';
        default:
          return 'Ex: Introduction générale';
      }
    },
  },
};
</script>

<template>
  <div class="modal-card" style="width: 800px;">
    <header class="modal-card-head">
      <p class="modal-card-title">
        <b-icon icon="magic" class="mr-2" />
        Générateur de structure de formation
      </p>
    </header>

    <section ref="modalBody" class="modal-card-body">
      <div class="content">
        <div>
          <div class="mb-4">
            <h4 class="mb-1">
              Structure de la formation
            </h4>
            <p class="help mb-0">
              Organisez votre formation en chapitres ou créez des leçons et
              activités pédagogiques  indépendantes.
              Réorganisez facilement les éléments par glisser-déposer.
            </p>
            <p v-if="training.items.length > 0" class="box is-warning mt-3">
              Vous avez déjà une structure. Cette nouvelle structure sera ajoutée en dessous des contenus existants.
            </p>
          </div>

          <div v-if="items.length === 0 && !isEditorOpen" class="py-5">
            <div class="columns">
              <div class="column is-6">
                <div class="box has-text-left h-full is-flex is-flex-direction-column">
                  <h5 class="has-text-weight-semibold mb-2 has-text-dark">
                    Génération assistée par IA
                  </h5>
                  <p class="is-size-7 has-text-grey-dark mb-3 is-flex-grow-1">
                    Décrivez votre cible et vos objectifs pédagogiques :
                    l'IA vous propose une structure claire et cohérente, que vous pourrez ensuite ajuster.
                  </p>
                  <div>
                    <b-button type="is-primary" icon-right="magic" @click="openAIGenerator">
                      Générer avec l'IA
                    </b-button>
                  </div>
                </div>
              </div>

              <div class="column is-6">
                <div class="box has-text-left h-full is-flex is-flex-direction-column">
                  <h5 class="has-text-weight-semibold mb-2 has-text-dark">
                    Création rapide par plan
                  </h5>
                  <p class="is-size-7 has-text-grey-dark mb-3 is-flex-grow-1">
                    Créez votre structure en ajoutant chapitres et leçons selon votre propre plan pédagogique,
                    il ne vous restera plus qu'à ajouter vos contenus !
                  </p>
                  <div>
                    <b-button type="is-primary" icon-right="plus-circle" @click="isEditorOpen = true">
                      Créer votre structure
                    </b-button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Draggable v-else v-model="items" handle=".item-handle" :animation="200">
            <div v-for="(item, itemIndex) in items" :key="item.id" class="box mb-3"
              :class="{ 'is-light': item.type === 'GENERIC' }">
              <template v-if="item.type === 'SECTION'">
                <div class="is-flex is-justify-content-space-between is-align-items-start mb-3">
                  <div class="is-flex is-align-items-center">
                    <b-icon icon="grip-lines" class="item-handle mr-2 has-text-black30" />
                    <b-icon icon="folder" class="mr-2" />
                    <strong>#{{ itemIndex + 1 }}</strong>
                  </div>
                  <b-button
                    class="is-size-9"
                    type="is-danger"
                    size="is-small"
                    icon-left="trash"
                    @click="removeItem(itemIndex)"
                  />
                </div>

                <b-field label="Titre du chapitre" :label-for="`section-title-${item.id}`">
                  <b-input
                    v-model="item.name"
                    :id="`section-title-${item.id}`"
                    placeholder="Ex: Introduction au marketing digital"
                  />
                </b-field>

                <div class="mt-4">
                  <div class="is-flex is-justify-content-space-between is-align-items-center mb-2">
                    <strong class="is-size-6">Contenu du chapitre</strong>
                  </div>

                  <div v-if="item.children.length === 0" class="notification">
                    Aucun contenu pour ce chapitre
                  </div>
                  <Draggable v-else v-model="item.children" handle=".lesson-handle" :animation="200">
                    <div v-for="(lesson, lessonIndex) in item.children" :key="lesson.id" class="notification p-5">
                      <div class="is-flex is-justify-content-space-between is-align-items-start mb-2">
                        <div class="is-flex is-align-items-center">
                          <b-icon icon="grip-lines" class="lesson-handle mr-2 has-text-black70" />
                          <template v-if="lesson.type === 'QUIZ'">
                            <b-icon icon="tasks" class="mr-2" />
                            <span>#{{ lessonIndex + 1 }}</span>
                          </template>
                          <template v-else-if="lesson.type === 'TASKS'">
                            <b-icon icon="clipboard-check" class="mr-2" />
                            <span>#{{ lessonIndex + 1 }}</span>
                          </template>
                          <template v-else-if="lesson.type === 'ASSIGNMENT'">
                            <b-icon icon="file-archive" class="mr-2" />
                            <span>#{{ lessonIndex + 1 }}</span>
                          </template>
                          <template v-else>
                            <b-icon icon="file-invoice" class="mr-2" />
                            <span>#{{ lessonIndex + 1 }}</span>
                          </template>
                        </div>
                        <b-button
                          class="is-size-9"
                          type="is-danger"
                          size="is-small"
                          icon-left="trash"
                          @click="removeLesson(itemIndex, lessonIndex)"
                        />
                      </div>

                      <b-field :label="getItemLabel(lesson.type)" :label-for="`lesson-title-${lesson.id}`">
                        <b-input
                          :ref="`lesson-title-${lesson.id}`"
                          v-model="lesson.name"
                          :id="`lesson-title-${lesson.id}`"
                          :placeholder="getItemPlaceholder(lesson.type)"
                        />
                      </b-field>

                    </div>
                  </Draggable>
                  <div class="mt-3 has-text-right">
                    <TrainingContentDropdown
                      :items="items"
                      is-section
                      position="is-top-right"
                      @add-lesson="addLesson(itemIndex)"
                      @add-quiz="addQuiz(itemIndex)"
                      @add-tasks="addTasks(itemIndex)"
                      @add-assignment="addAssignment(itemIndex)"
                    />
                  </div>
                </div>
              </template>

              <template v-else>
                <div class="is-flex is-justify-content-space-between is-align-items-start mb-3">
                  <div class="is-flex is-align-items-center">
                    <b-icon icon="grip-lines" class="item-handle mr-2 has-text-black30" />
                    <template v-if="item.type === 'QUIZ'">
                      <b-icon icon="tasks" class="mr-2" />
                      <strong>#{{ itemIndex + 1 }}</strong>
                    </template>
                    <template v-else-if="item.type === 'TASKS'">
                      <b-icon icon="clipboard-check" class="mr-2" />
                      <strong>#{{ itemIndex + 1 }}</strong>
                    </template>
                    <template v-else-if="item.type === 'ASSIGNMENT'">
                      <b-icon icon="file-archive" class="mr-2" />
                      <strong>#{{ itemIndex + 1 }}</strong>
                    </template>
                    <template v-else>
                      <b-icon icon="file-invoice" class="mr-2" />
                      <strong>#{{ itemIndex + 1 }}</strong>
                    </template>
                  </div>
                  <b-button
                    class="is-size-9"
                    type="is-danger"
                    size="is-small"
                    icon-left="trash"
                    @click="removeItem(itemIndex)"
                  />
                </div>

                <b-field :label="getItemLabel(item.type)" :label-for="`item-title-${item.id}`">
                  <b-input
                    v-model="item.name"
                    :id="`item-title-${item.id}`"
                    :placeholder="getItemPlaceholder(item.type)"
                  />
                </b-field>

              </template>
            </div>
          </Draggable>

          <div v-if="items.length > 0 || isEditorOpen" class="buttons mt-6">
            <TrainingContentDropdown :items="items" position="is-top-left" @add-lesson="addLesson()" @add-chapter="addChapter()" @add-quiz="addQuiz()" @add-tasks="addTasks()" @add-assignment="addAssignment()" />
          </div>
        </div>
      </div>
    </section>

    <footer class="modal-card-foot is-justify-content-flex-end btop-grey">
      <b-button type="is-text" @click="confirmClose">Annuler</b-button>
      <b-button type="is-primary" :loading="isLoading" :disabled="items.length === 0"
        @click="createStructure">
        <template v-if="training.items.length > 0">
          Ajouter à votre structure
        </template>
        <template v-else>
          Créer votre structure
        </template>
      </b-button>
    </footer>
  </div>
</template>

<style lang="scss" scoped>
.item-handle,
.lesson-handle {
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
}

::v-deep .sortable-ghost {
  opacity: 0.5;
  border: 2px dashed $primary;
}
</style>
